import { useQuery } from '@tanstack/react-query'
import { useCallback } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { QueryKeys } from '../../../../constants/queryKeys.constants'
import { getBeachPlanConfiguration, updateBeachPlanConfiguration, uploadImage } from '../../services/configuration.service'
import { ConfigurationCategory } from '../../types/configuration.types'
import { BeachPlanConfigurationType } from '../types/types'
import { BeachAsset } from '../../../BeachPlan/jotai-modules/types'

export const useBeachPlanConfiguration = (enabled = true) => {
  const queryClient = useQueryClient()

  const { data: configuration, isLoading } = useQuery<BeachPlanConfigurationType>({
    queryKey: [QueryKeys.CONFIGURATION, ConfigurationCategory.BEACH_PLAN],
    queryFn: getBeachPlanConfiguration,
    enabled,
  })

  // ---------------------------- Update Configuration ----------------------------
  const update = useCallback(
    async (data: Partial<BeachPlanConfigurationType>) => {
      await updateBeachPlanConfiguration(data)
      queryClient.invalidateQueries({ queryKey: [QueryKeys.CONFIGURATION, ConfigurationCategory.BEACH_PLAN] })
    },
    [queryClient]
  )

  // ---------------------------- Upload Background ----------------------------
  const uploadBackground = useCallback(
    async (file: File) => {
      const imageUrl = await uploadImage(file, ConfigurationCategory.BEACH_PLAN)
      await update({ background: imageUrl })
    },
    [update]
  )

  // ---------------------------- Delete All Spots ----------------------------
  const deleteAllSpots = useCallback(async () => {
    await update({ spots: [] })
  }, [update])

  const spots: BeachAsset[] = configuration?.spots ?? []

  return {
    spots,
    rows: configuration?.rows || 0,
    columns: configuration?.columns || 0,
    background: configuration?.background || '',
    isLoading,
    update,
    uploadBackground,
    deleteAllSpots,
    backgroundFilename: getOriginalFileName(configuration?.background || ''),
  }
}

//-------------------- Helper Functions --------------------
const getOriginalFileName = (url: string) => {
  if (!url) return ''
  const decodedUrl = decodeURIComponent(url)
  const fileName = decodedUrl.split('/').pop()?.split('?')[0] || ''
  return fileName.replace(/-(\d+)\./, '.')
}
